import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { CloudArrowUp, CloudArrowDown, Clock, Database, Lock, Trash, Warning, Spinner } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(dateStr) {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

function BackupTypeIcon({ type }) {
  switch (type) {
    case "nightly":
      return <Clock size={14} className="text-blue-400" />;
    case "manual":
      return <CloudArrowUp size={14} className="text-emerald-400" />;
    case "pre_restore_safety":
      return <Lock size={14} className="text-amber-400" />;
    default:
      return <Database size={14} className="text-zinc-400" />;
  }
}

function BackupTypeBadge({ type }) {
  const colors = {
    nightly: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    manual: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    pre_restore_safety: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  };
  return (
    <Badge variant="outline" className={colors[type] || "bg-zinc-700 text-zinc-300"}>
      <BackupTypeIcon type={type} />
      <span className="ml-1">{type?.replace(/_/g, " ") || "unknown"}</span>
    </Badge>
  );
}

function RestoreConfirmModal({ backup, onConfirm, onCancel, isRestoring }) {
  const [confirmText, setConfirmText] = useState("");
  const canConfirm = confirmText === "RESTORE";

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center gap-3 text-amber-400 mb-4">
          <Warning size={24} />
          <h3 className="text-lg font-semibold">Confirm Restore</h3>
        </div>
        
        <p className="text-zinc-300 mb-4">
          This will restore the tenant to backup <span className="font-mono text-sm bg-zinc-800 px-1 rounded">{backup?.backup_id}</span> from{" "}
          <span className="text-white">{formatDate(backup?.created_at)}</span>.
        </p>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded p-3 mb-4">
          <p className="text-red-400 text-sm">
            <strong>Warning:</strong> This will delete all current tenant data and replace it with the backup snapshot. 
            A safety backup will be created first.
          </p>
        </div>
        
        <div className="mb-4">
          <label className="text-sm text-zinc-400 block mb-2">
            Type <span className="font-mono bg-zinc-800 px-1 rounded">RESTORE</span> to confirm:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white focus:border-amber-500 focus:outline-none"
            placeholder="Type RESTORE"
            disabled={isRestoring}
            data-testid="restore-confirm-input"
          />
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isRestoring}
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={!canConfirm || isRestoring}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
            data-testid="restore-confirm-btn"
          >
            {isRestoring ? (
              <>
                <Spinner size={16} className="mr-2 animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <CloudArrowDown size={16} className="mr-2" />
                Restore
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function TenantBackupsTab({ tenantId, companyName }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [lastActivity, setLastActivity] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    if (tenantId) {
      fetchBackups();
    }
  }, [tenantId]);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/admin/tenants/${tenantId}/backups`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setBackups(data.backups || []);
        setIsLocked(data.is_locked || false);
        setLastActivity(data.last_activity_at);
      }
    } catch (error) {
      console.error("Error fetching backups:", error);
      toast.error("Failed to load backups");
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    try {
      setCreating(true);
      const response = await fetch(`${API}/admin/tenants/${tenantId}/backups`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Backup created: ${data.backup?.total_documents || 0} documents`);
        fetchBackups();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create backup");
      }
    } catch (error) {
      console.error("Error creating backup:", error);
      toast.error("Failed to create backup");
    } finally {
      setCreating(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreModal) return;
    
    try {
      setIsRestoring(true);
      const response = await fetch(
        `${API}/admin/tenants/${tenantId}/backups/${restoreModal.backup_id}/restore`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirm: "RESTORE" }),
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Restore completed successfully");
        setRestoreModal(null);
        fetchBackups();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Restore failed");
      }
    } catch (error) {
      console.error("Error restoring:", error);
      toast.error("Restore failed");
    } finally {
      setIsRestoring(false);
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("Delete this backup? This cannot be undone.")) return;
    
    try {
      const response = await fetch(
        `${API}/admin/tenants/${tenantId}/backups/${backupId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      
      if (response.ok) {
        toast.success("Backup deleted");
        fetchBackups();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete backup");
      }
    } catch (error) {
      console.error("Error deleting backup:", error);
      toast.error("Failed to delete backup");
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-16 bg-zinc-800 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tenant-backups-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Backups</h3>
          <p className="text-sm text-zinc-500">
            {backups.length} backup{backups.length !== 1 ? "s" : ""} stored
            {lastActivity && (
              <span className="ml-2">
                • Last activity: {formatDate(lastActivity)}
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={createBackup}
          disabled={creating || isLocked}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="create-backup-btn"
        >
          {creating ? (
            <>
              <Spinner size={16} className="mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <CloudArrowUp size={16} className="mr-2" />
              Create Backup
            </>
          )}
        </Button>
      </div>

      {/* Lock warning */}
      {isLocked && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 flex items-center gap-3">
          <Lock size={20} className="text-amber-400" />
          <div>
            <p className="text-amber-400 font-medium">Tenant is locked</p>
            <p className="text-sm text-zinc-400">
              A restore operation may be in progress. Writes are blocked.
            </p>
          </div>
        </div>
      )}

      {/* Backups list */}
      {backups.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          <Database size={48} className="mx-auto mb-3 opacity-50" />
          <p>No backups yet</p>
          <p className="text-sm">Create a manual backup or wait for the nightly job.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {backups.map((backup) => (
            <div
              key={backup.backup_id}
              className="bg-zinc-800 border border-zinc-700 rounded p-4 flex items-center justify-between"
              data-testid={`backup-row-${backup.backup_id}`}
            >
              <div className="flex items-center gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm text-zinc-300">
                      {backup.backup_id.slice(0, 16)}...
                    </span>
                    <BackupTypeBadge type={backup.backup_type} />
                    {backup.status !== "completed" && (
                      <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                        {backup.status}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-zinc-500 flex items-center gap-4">
                    <span>{formatDate(backup.created_at)}</span>
                    <span>{formatBytes(backup.size_bytes)}</span>
                    <span>{backup.total_documents || 0} documents</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {backup.status === "completed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRestoreModal(backup)}
                    disabled={isLocked}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                    data-testid={`restore-btn-${backup.backup_id}`}
                  >
                    <CloudArrowDown size={14} className="mr-1" />
                    Restore
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteBackup(backup.backup_id)}
                  className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`delete-btn-${backup.backup_id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Restore confirmation modal */}
      {restoreModal && (
        <RestoreConfirmModal
          backup={restoreModal}
          onConfirm={handleRestore}
          onCancel={() => setRestoreModal(null)}
          isRestoring={isRestoring}
        />
      )}
    </div>
  );
}
